import { useLocation } from "react-router-dom";

const SITE_URL = "https://perspectivetherapy.com";
const SHARE_IMAGE = `${SITE_URL}/images/homepage/homepage-supporting.png`;

const metadata: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Perspective Therapy | Therapy in NY, NJ & IN",
    description:
      "Perspective Therapy provides in-person therapy in Larchmont, NY and telehealth across New York, New Jersey, and Indiana for children, teens, adults, couples, and groups.",
  },
  "/about": {
    title: "About Perspective Therapy | In-Person & Telehealth Care",
    description:
      "Meet Perspective Therapy, serving children, teens, adults, couples, and groups in Larchmont and by telehealth in New York, New Jersey, and Indiana.",
  },
  "/therapists": {
    title: "Find a Therapist in NY, NJ & IN | Perspective Therapy",
    description:
      "Explore Perspective Therapy clinicians and filter by population, specialty, format, and current availability. Licensure and location eligibility vary by therapist.",
  },
  "/child-therapy": {
    title: "Child Therapy in Larchmont & via Telehealth | Perspective Therapy",
    description:
      "Warm, age-appropriate child therapy for ages 6–12, with in-person care in Larchmont and telehealth where clinically and legally appropriate.",
  },
  "/teen-therapy": {
    title: "Teen Therapy in Larchmont & NY, NJ, IN Telehealth",
    description:
      "Supportive teen therapy for ages 13–17. Perspective Therapy offers in-person sessions in Larchmont and telehealth in NY, NJ, and IN, based on clinician eligibility.",
  },
  "/individual-therapy": {
    title: "Individual Therapy in Larchmont & NY, NJ, IN Telehealth",
    description:
      "Individual therapy at Perspective Therapy, with in-person sessions in Larchmont and telehealth in New York, New Jersey, and Indiana based on therapist licensure.",
  },
  "/couples-therapy": {
    title: "Couples Therapy in Larchmont & NY, NJ, IN Telehealth",
    description:
      "Structured couples therapy at Perspective Therapy, available in Larchmont and by telehealth in NY, NJ, and IN when therapist location eligibility allows.",
  },
  "/group-therapy": {
    title: "Child Group Therapy in Larchmont | Perspective Therapy",
    description:
      "Explore Fall 2026 play-based emotional regulation and social skills groups for children ages 6–7 at Perspective Therapy in Larchmont, NY.",
  },
  "/contact": {
    title: "Contact Perspective Therapy | Larchmont & Telehealth",
    description:
      "Contact Perspective Therapy for in-person care in Larchmont, New York, or telehealth in NY, NJ, and IN. Therapist availability and state eligibility vary.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Perspective Therapy",
    description:
      "Learn how Perspective Therapy handles information submitted through its public website, blog subscriptions, email, and optional SMS reminders.",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | Perspective Therapy",
    description:
      "Review the terms governing use of the Perspective Therapy public website and its optional email and appointment-reminder communications.",
  },
};

export default function SiteMetadata() {
  const { pathname } = useLocation();
  const page = metadata[pathname];
  if (!page) return null;

  const canonicalPath = pathname;
  const canonical = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;
  return (
    <>
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={SHARE_IMAGE} />
      <meta property="og:image:alt" content="Perspective Therapy's welcoming therapy practice" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={SHARE_IMAGE} />
      {import.meta.env.VITE_GOOGLE_SITE_VERIFICATION?.trim() && (
        <meta name="google-site-verification" content={import.meta.env.VITE_GOOGLE_SITE_VERIFICATION.trim()} />
      )}
    </>
  );
}
